/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Box, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

type CopyableIdFieldProps = {
  label: string
  value: string
  inline?: boolean
  maxWidth?: number | string
}

function CopyableIdField({
  label,
  value,
  inline = false,
  maxWidth,
}: CopyableIdFieldProps) {
  const { t } = useT()
  const [tooltipOpen, setTooltipOpen] = React.useState(false)
  const timeoutRef = React.useRef<number | null>(null)

  React.useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setTooltipOpen(true)
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => {
        setTooltipOpen(false)
      }, 1200)
    } catch (error) {
      console.error('Failed to copy ID to clipboard', error)
    }
  }

  return (
    <Tooltip
      title={t('common.copied', 'Copied!')}
      open={tooltipOpen}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement="top"
    >
      <Box
        onClick={() => void handleCopy()}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            void handleCopy()
          }
        }}
        sx={theme => ({
          minWidth: 0,
          maxWidth,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: 1.5,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        {inline ? (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                fontFamily:
                  'ui-monospace, SFMono-Regular, SF Mono, Consolas, monospace',
                fontSize: '0.8rem',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={value}
            >
              {value}
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                fontFamily:
                  'ui-monospace, SFMono-Regular, SF Mono, Consolas, monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={value}
            >
              {value}
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  )
}

export default CopyableIdField
